import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
  Container,
  Fade,
  Grow,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Grid,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';

import { GET, POST, removeLocalAccessTokenAndUser } from '../../configs/api';
import { api } from '../../utils/constants';
import { getUser, removeUser, setUser } from '../../store/slices/userSlice';
import { setSelectedCompany, clearSelectedCompany } from '../../store/slices/tenantSlice';
import { removeClients } from '../../store/slices/clientsSlice';
import { removeUsers } from '../../store/slices/usersSlice';
import { removeJobs } from '../../store/slices/jobsSlice';
import { toggleTheme } from '../../store/slices/themeSlice';
import CompanyPickerAmbientBg, { useCompanyPickerSurfaces } from './CompanyPickerAmbientBg';

const CompanyLogoAvatar = ({ company, isDarkMode, size = 64 }) => {
  const [logoFailed, setLogoFailed] = useState(false);
  const logoUrl = company.logo_download_url;
  const showLogo = logoUrl && !logoFailed;

  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        flexShrink: 0,
        overflow: 'hidden',
        background: showLogo
          ? (isDarkMode ? 'rgba(255, 255, 255, 0.95)' : '#ffffff')
          : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: isDarkMode
          ? (showLogo ? '1px solid #334155' : '1px solid rgba(59, 130, 246, 0.45)')
          : (showLogo ? '1px solid rgba(0, 0, 0, 0.08)' : 'none'),
        boxShadow: isDarkMode
          ? '0 8px 24px rgba(15, 23, 42, 0.45)'
          : '0 8px 24px rgba(59, 130, 246, 0.3)',
      }}
    >
      {showLogo ? (
        <Box
          component="img"
          src={logoUrl}
          alt={`${company.name} logo`}
          onError={() => setLogoFailed(true)}
          sx={{ width: '100%', height: '100%', objectFit: 'contain', p: 0.5 }}
        />
      ) : (
        <BusinessIcon sx={{ fontSize: size * 0.5, color: 'white' }} />
      )}
    </Box>
  );
};

/**
 * Company picker for multi-company members.
 * Only lists companies the user has membership access to; enter re-issues the access JWT.
 */
const UsersCompanyPicker = () => {
  const dispatch = useDispatch();
  const user = useSelector(getUser);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { glassCard, companyCardHover, primaryCtaSx } = useCompanyPickerSurfaces();

  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enteringId, setEnteringId] = useState(null);
  const [toast, setToast] = useState({ open: false, severity: 'success', message: '' });

  const showToast = (severity, message) => setToast({ open: true, severity, message });

  const loadCompanies = async () => {
    setLoading(true);
    const res = await GET(api.myCompanies);
    setLoading(false);
    if (res.status === 200) {
      setCompanies(res.data?.companies || []);
    } else {
      // Fall back to companies embedded on the user payload from login/validate.
      const fromUser = Array.isArray(user.companies) ? user.companies : [];
      setCompanies(fromUser);
      if (!fromUser.length) {
        showToast('error', res.message || 'Failed to load companies.');
      }
    }
  };

  useEffect(() => {
    loadCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEnter = async (company) => {
    setEnteringId(company.id);
    const res = await POST(api.switchCompany, {}, { company_id: company.id });
    setEnteringId(null);
    if (res.status === 200 && res.data?.accessToken) {
      dispatch(removeClients());
      dispatch(removeUsers());
      dispatch(removeJobs());
      dispatch(setUser({ ...res.data.user, accessToken: res.data.accessToken }));
      dispatch(setSelectedCompany({ id: company.id, name: company.name }));
    } else {
      showToast('error', res.message || 'Failed to switch company.');
    }
  };

  const handleLogout = async () => {
    await GET(api.logout);
    removeLocalAccessTokenAndUser();
    dispatch(clearSelectedCompany());
    dispatch(removeUser());
  };

  const actionButtonSx = {
    borderRadius: 2,
    textTransform: 'none',
    fontWeight: 600,
    px: 2.5,
    py: 1,
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', position: 'relative', overflow: 'hidden' }}>
      <CompanyPickerAmbientBg />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, py: { xs: 3, md: 5 }, px: { xs: 2, md: 4 } }}>
        <Fade in timeout={600}>
          <Box
            sx={{
              ...glassCard,
              p: { xs: 2.5, md: 3.5 },
              mb: 4,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
              <Box
                sx={{
                  width: isMobile ? 56 : 72,
                  height: isMobile ? 56 : 72,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: isDarkMode ? '1px solid rgba(59, 130, 246, 0.45)' : 'none',
                  boxShadow: '0 12px 40px rgba(59, 130, 246, 0.35)',
                  animation: 'companyPickerIconPulse 3s ease-in-out infinite',
                  flexShrink: 0,
                }}
              >
                <BusinessIcon sx={{ fontSize: isMobile ? 28 : 36, color: 'white' }} />
              </Box>
              <Box>
                <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 800, mb: 0.5, color: 'text.primary' }}>
                  Select a company
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Signed in as {user.email} · Choose a workspace to continue
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
              <Tooltip title={isDarkMode ? 'Light mode' : 'Dark mode'}>
                <IconButton
                  onClick={() => dispatch(toggleTheme())}
                  sx={{
                    color: 'text.primary',
                    background: isDarkMode ? 'rgba(51, 65, 85, 0.55)' : 'rgba(0, 0, 0, 0.04)',
                    border: isDarkMode ? `1px solid ${theme.palette.divider}` : '1px solid rgba(0, 0, 0, 0.08)',
                    '&:hover': {
                      background: isDarkMode ? 'rgba(51, 65, 85, 0.8)' : 'rgba(0, 0, 0, 0.08)',
                    },
                  }}
                >
                  {isDarkMode ? <LightModeIcon /> : <DarkModeIcon />}
                </IconButton>
              </Tooltip>
              <Button
                variant="outlined"
                startIcon={<LogoutIcon />}
                onClick={handleLogout}
                sx={{
                  ...actionButtonSx,
                  color: 'text.primary',
                  borderColor: isDarkMode ? theme.palette.divider : 'rgba(0, 0, 0, 0.15)',
                  '&:hover': {
                    borderColor: isDarkMode ? 'rgba(148, 163, 184, 0.55)' : 'rgba(0, 0, 0, 0.3)',
                    background: isDarkMode ? 'rgba(51, 65, 85, 0.45)' : 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                Log out
              </Button>
            </Box>
          </Box>
        </Fade>

        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 12, gap: 2 }}>
            <CircularProgress sx={{ color: 'primary.main' }} />
            <Typography color="text.secondary">Loading companies…</Typography>
          </Box>
        ) : companies.length === 0 ? (
          <Grow in timeout={800}>
            <Box sx={{ ...glassCard, p: { xs: 5, md: 8 }, textAlign: 'center', maxWidth: 560, mx: 'auto' }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: 'text.primary' }}>
                No companies available
              </Typography>
              <Typography color="text.secondary">
                Your account is not assigned to any company. Contact your administrator.
              </Typography>
            </Box>
          </Grow>
        ) : (
          <Grid container spacing={3}>
            {companies.map((company, index) => (
              <Grid item xs={12} sm={6} key={company.id}>
                <Fade in timeout={600} style={{ transitionDelay: `${index * 80}ms` }}>
                  <Box
                    sx={{
                      ...glassCard,
                      ...companyCardHover,
                      p: { xs: 3, md: 4 },
                      minHeight: 200,
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 3 }}>
                      <CompanyLogoAvatar company={company} isDarkMode={isDarkMode} />
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 700,
                          color: 'text.primary',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {company.name}
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      startIcon={<LoginIcon />}
                      disabled={enteringId === company.id}
                      onClick={() => handleEnter(company)}
                      sx={{
                        ...actionButtonSx,
                        mt: 'auto',
                        alignSelf: 'flex-end',
                        ...primaryCtaSx,
                      }}
                    >
                      {enteringId === company.id ? 'Entering…' : 'Enter'}
                    </Button>
                  </Box>
                </Fade>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UsersCompanyPicker;
